import type { User } from '@/api/types'

/**
 * The signed-in session, kept in localStorage so a reload stays signed in.
 * Access tokens live 15 minutes and are refreshed with the 7-day refresh
 * token (see api/client.ts).
 */
export interface StoredSession {
  accessToken: string
  refreshToken: string | null
  user: User
}

const KEY = 'saadhan.session'
const SCOPE_KEY = 'saadhan.cpoScope'

type Listener = (session: StoredSession | null) => void
const listeners = new Set<Listener>()

let current: StoredSession | null = read()
let contextVersion = 0

function read(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as StoredSession) : null
  } catch {
    return null
  }
}

export const session = {
  get: () => current,
  version: () => contextVersion,
  set(next: StoredSession | null) {
    if (next?.user.id !== current?.user.id || next?.user.role !== current?.user.role || next?.user.viewer_station_id !== current?.user.viewer_station_id || next?.refreshToken !== current?.refreshToken) contextVersion++
    if (!next || !current || next.user.id !== current.user.id || next.user.role !== current.user.role) cpoScope.set(null)
    current = next
    try {
      if (next) localStorage.setItem(KEY, JSON.stringify(next))
      else {
        localStorage.removeItem(KEY)
        localStorage.removeItem(SCOPE_KEY)
      }
    } catch {
      /* private mode: the session lasts until reload */
    }
    listeners.forEach((l) => l(next))
  },
  updateAccessToken(token: string) {
    if (current) session.set({ ...current, accessToken: token })
  },
  subscribe(listener: Listener) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/**
 * Which operator a superadmin is looking at. Every /cpo/* request carries it
 * as `cpo_id` (the backend requires it for superadmins).
 */
type ScopeListener = (cpoId: number | null) => void
const scopeListeners = new Set<ScopeListener>()
let scopeVersion = 0
let scope: number | null = (() => {
  try {
    const v = Number(localStorage.getItem(SCOPE_KEY))
    return Number.isFinite(v) && v > 0 ? v : null
  } catch {
    return null
  }
})()

export const cpoScope = {
  get: () => scope,
  version: () => scopeVersion,
  set(cpoId: number | null) {
    if (scope === cpoId) return
    scopeVersion++
    scope = cpoId
    try {
      if (cpoId) localStorage.setItem(SCOPE_KEY, String(cpoId))
      else localStorage.removeItem(SCOPE_KEY)
    } catch {
      /* ignore */
    }
    scopeListeners.forEach((l) => l(cpoId))
  },
  subscribe(listener: ScopeListener) {
    scopeListeners.add(listener)
    return () => {
      scopeListeners.delete(listener)
    }
  },
}
